import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { useFetch } from "@/hooks/useFetch";
import { Seo } from "@/components/site/Seo";
import { PageHero } from "@/components/site/PageHero";
import { Markdown } from "@/components/site/Markdown";
import { Reveal } from "@/components/site/Reveal";
import { SectionLabel } from "@/components/site/Img";
import { DealerForm } from "@/components/forms/DealerForm";
import { PageNotice } from "@/components/site/PageNotice";
import { PHONE, PHONE_HREF } from "@/lib/api";

const STEPS = [
  ["ᚠ", "Talk it through", "Your area, the vehicles you'll see and your budget."],
  ["ᚢ", "Choose equipment", "Master or slave, from the leading tool brands we supply."],
  ["ᚦ", "Setup & training", "We configure the tool and walk you through the workflow."],
  ["ᚨ", "Start tuning", "Upload reads to the portal; files come back from our team."],
];

export default function DealerPage() {
  const { data: page } = useFetch("/pages/dealer");
  return (
    <div data-testid="dealer-page">
      <Seo path="/remappingfranchise" />
      <PageNotice page={page} />
      <PageHero page={page} crumbs={[["/", "Home"], [null, "Become a Dealer"]]}>
        <div className="mt-8 flex flex-wrap gap-3">
          <a href="#apply" className="btn-ice" data-testid="dealer-apply-scroll">Apply now</a>
          <a href={PHONE_HREF} className="btn-ghost" data-testid="dealer-call">Call {PHONE}</a>
        </div>
      </PageHero>
      <section className="container-x py-20">
        <div className="grid gap-px bg-[color:var(--line)] sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map(([r, t, d], i) => (
            <Reveal key={t} delay={i * 90} className="bg-[color:var(--bg)] p-7">
              <div className="flex items-center justify-between font-mono text-xs"><span className="text-[color:var(--ice)]">0{i + 1}</span><span className="text-[color:var(--bronze)]">{r}</span></div>
              <h2 className="mt-8 font-display text-2xl font-semibold uppercase">{t}</h2>
              <p className="mt-2 text-sm text-slate-400">{d}</p>
            </Reveal>
          ))}
        </div>
      </section>
      <section className="container-x grid gap-16 pb-12 lg:grid-cols-[1fr_1.05fr]">
        <Reveal><Markdown testId="page-body">{page?.body}</Markdown></Reveal>
        <Reveal delay={120} id="apply" className="scroll-mt-28">
          <div className="panel chamfer relative p-6 sm:p-10">
            <div className="knot-bg pointer-events-none absolute inset-0 opacity-[0.03]" />
            <div className="relative">
              <SectionLabel rune="ᛟ" bronze>Dealer application</SectionLabel>
              <h2 className="mb-2 mt-4 font-display text-3xl font-bold uppercase">Apply to join</h2>
              <p className="mb-8 text-sm text-slate-400">No obligation. We'll call to talk through options.</p>
              <ul className="mb-8 grid gap-2 text-sm text-slate-300 sm:grid-cols-2">
                {["Choice of equipment", "Transparent file pricing", "Pay as you go", "Full setup"].map((x) => (
                  <li key={x} className="flex items-center gap-2"><Check className="h-4 w-4 text-[color:var(--ice)]" />{x}</li>
                ))}
              </ul>
              <DealerForm />
            </div>
          </div>
        </Reveal>
      </section>
      <section className="container-x py-12 text-sm text-slate-500">
        Prefer to browse first? See the <Link className="text-[color:var(--ice)] underline" to="/tuning-tools">tools we supply</Link>.
      </section>
    </div>
  );
}
